import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useClinica, type Sede } from "../clinica.ts";
import { api, puede, ErrorApi, enviarMensajeAhora, type Cita, type Profesional, type Paciente } from "../api.ts";
import { inicioSemana, sumarDias, columnasDeCitas, fechaLarga, horaLarga, interpretarCita, NOMBRE_ESTADO_CITA, TRANSICIONES, DURACIONES, aLocal, norm } from "../../../dominio/src/index.ts";
import { Dictador } from "../componentes/Dictador.tsx";
import { leerCache, guardarCache } from "../cache.ts";
import { AvisoError, hoy, Seccion, useAccion } from "./comun.tsx";
import { AvisoConDuplicado, esErrorDuplicado } from "../componentes/AvisoDuplicado.tsx";
import { FechaInput } from "../componentes/CampoFecha.tsx";
import { AgendaGoogle } from "./AgendaGoogle.tsx";

const COLORES = ["#0A6E5C", "#2459C9", "#935400", "#8C3FA8", "#C8352E", "#3F7D20"];
const DIAS_CORTOS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const INICIO_GRILLA = 7 * 60, FIN_GRILLA = 20 * 60, PX_MIN = 1.1;
const minutos = (h: string) => { const [a, b] = h.split(":").map(Number); return a * 60 + b; };

const leerLocal = (k: string) => { try { return localStorage.getItem(k) ?? ""; } catch { return ""; } };
const escribirLocal = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* opcional */ } };
/** «Sede Norte» → «SN»: para la etiqueta pequeña de cada cita. */
const siglasSede = (n: string) => n.split(/\s+/).filter(Boolean).map((w) => w[0]).join("").slice(0, 3).toUpperCase();
interface FeriadoDia { fecha: string; nombre: string; tipo: "nacional" | "propio"; nota?: string }
interface Formulario { id?: string; paciente: { id: string; nombre: string } | null; profesionalId: string; sedeId: string; fecha: string; hora: string; duracion: number; motivo: string; notas: string }

export function Agenda({ ir, pacienteInicial }: { ir: (r: string) => void; pacienteInicial?: string }) {
  // «Mi agenda» abre una cita en su día: #/agenda?dia=aaaa-mm-dd
  const [dia, setDia] = useState(() => { const d = new URLSearchParams(location.hash.split("?")[1] ?? "").get("dia") ?? ""; return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : hoy(); });
  const [vista, setVista] = useState<"semana" | "dia">(() => (typeof window !== "undefined" && window.innerWidth < 760 ? "dia" : "semana"));
  const [profs, setProfs] = useState<Profesional[]>([]);
  const [filtro, setFiltro] = useState("");
  // las sedes son de una misma clínica (mismo dueño): la agenda las muestra todas y se puede filtrar por sede
  const clinica = useClinica();
  // el personal de otra sede (no la matriz) ve solo la agenda de su sede; la matriz y la administración ven todas
  const [alcance, setAlcance] = useState<string | null>(null);
  useEffect(() => { api<{ sedeId: string | null }>("GET", "/api/agenda/alcance").then((r) => setAlcance(r.sedeId)).catch(() => undefined); }, []);
  const sedes = (clinica?.sedes ?? []).filter((x) => x.activa && (!alcance || x.id === alcance));
  const variasSedes = sedes.length > 1;
  const [sedeGuardada, setSedeGuardada] = useState(() => leerLocal("donna.agenda.sede"));
  const filtroSede = variasSedes && sedes.some((x) => x.id === sedeGuardada) ? sedeGuardada : "";
  const elegirSede = (v: string) => { setSedeGuardada(v); escribirLocal("donna.agenda.sede", v); };
  const sedeInicial = () => (filtroSede || (sedes.find((x) => x.id === leerLocal("donna.agenda.ultima-sede"))?.id ?? sedes[0]?.id ?? ""));
  const [citas, setCitas] = useState<Cita[]>([]);
  const [form, setForm] = useState<Formulario | null>(null);
  const [panelGoogle, setPanelGoogle] = useState(false);
  const [sel, setSel] = useState<Cita | null>(null);
  // feriados de Ecuador (y los días propios de la clínica) de los días que se ven
  const [feriados, setFeriados] = useState<Record<string, FeriadoDia>>({});

  const a = useAccion();
  const escribe = puede("agenda:escribir");

  // el domingo la clínica no atiende: la vista de semana salta a la semana siguiente
  const lunes = new Date(dia + "T12:00:00Z").getUTCDay() === 0 ? sumarDias(dia, 1) : inicioSemana(dia);
  const dias = vista === "semana" ? Array.from({ length: 6 }, (_, i) => sumarDias(lunes, i)) : [dia];
  const colorDe = useMemo(() => Object.fromEntries(profs.map((p, i) => [p.id, p.color || COLORES[i % COLORES.length]])), [profs]);

  const cargar = () => api<Cita[]>("GET", `/api/citas?desde=${dias[0]}&hasta=${dias[dias.length - 1]}${filtro ? `&profesional=${filtro}` : ""}${filtroSede ? `&sede=${filtroSede}` : ""}`).then(setCitas).catch(a.setError);
  useEffect(() => { api<Profesional[]>("GET", "/api/profesionales").then(setProfs).catch(a.setError); }, []);
  useEffect(() => { cargar(); }, [dias[0], dias.length, filtro, filtroSede]);
  useEffect(() => { api<FeriadoDia[]>("GET", `/api/agenda/feriados?desde=${dias[0]}&hasta=${dias[dias.length - 1]}`).then((l) => setFeriados(Object.fromEntries(l.map((x) => [x.fecha, x])))).catch(() => setFeriados({})); }, [dias[0], dias.length]);
  useEffect(() => {
    if (!pacienteInicial || !profs.length) return;
    api<Paciente>("GET", `/api/pacientes/${pacienteInicial}`).then((p) => nueva(hoy(), "09:00", { id: p.id, nombre: `${p.nombres} ${p.apellidos}` })).catch(() => undefined);
  }, [pacienteInicial, profs.length]);

  function nueva(fecha: string, hora: string, paciente: Formulario["paciente"] = null) {
    setSel(null);
    setForm({ paciente, profesionalId: filtro || profs[0]?.id || "", sedeId: sedeInicial(), fecha, hora, duracion: 30, motivo: "", notas: "" });
  }
  function editar(c: Cita) {
    setSel(null);
    setForm({ id: c.id, paciente: { id: c.paciente_id, nombre: c.paciente }, profesionalId: c.profesional_id, sedeId: c.sede_id ?? sedeInicial(), fecha: c.fecha, hora: c.hora, duracion: c.duracion, motivo: c.motivo, notas: c.notas });
  }
  async function cambiarEstado(c: Cita, estado: Cita["estado"], motivo = "") {
    const r = await a.correr(() => api<Cita>("POST", `/api/citas/${c.id}/estado`, { estado, motivo }));
    if (r) { setSel(r); cargar(); }
  }
  async function eliminarCita(c: Cita) {
    if (await a.correr(() => api("POST", `/api/citas/${c.id}/eliminar`))) { setSel(null); cargar(); }
  }
  const titulo = vista === "semana" ? `Semana del ${fechaLarga(lunes)}` : fechaLarga(dia)[0].toUpperCase() + fechaLarga(dia).slice(1);
  const ahora = aLocal(new Date());

  return (
    <>
      <div className="fila" style={{ justifyContent: "space-between" }}>
        <h1>Agenda</h1>
        <div className="fila">
          <button className="btn" onClick={() => setPanelGoogle(!panelGoogle)} aria-expanded={panelGoogle}>Ver en mi calendario</button>
          {escribe && <button className="btn pri" onClick={() => { const f = dia >= hoy() ? dia : hoy(); nueva(new Date(f + "T12:00:00Z").getUTCDay() === 0 ? sumarDias(f, 1) : f, "09:00"); }}>Nueva cita</button>}
        </div>
      </div>
      {panelGoogle && <AgendaGoogle cerrar={() => setPanelGoogle(false)} />}
      <div className="fila barra-agenda">
        <button className="btn chico" onClick={() => setDia(sumarDias(dia, vista === "semana" ? -7 : -1))} aria-label="Anterior">‹</button>
        <button className="btn chico" onClick={() => setDia(hoy())}>Hoy</button>
        <button className="btn chico" onClick={() => setDia(sumarDias(dia, vista === "semana" ? 7 : 1))} aria-label="Siguiente">›</button>
        <b style={{ flex: 1 }}>{titulo}</b>
        {variasSedes && (
          <select className="entrada" style={{ width: "auto" }} value={filtroSede} onChange={(e) => elegirSede(e.target.value)} aria-label="Sede">
            <option value="">Todas las sedes</option>{sedes.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}
          </select>
        )}
        <select className="entrada" style={{ width: "auto" }} value={filtro} onChange={(e) => setFiltro(e.target.value)} aria-label="Profesional">
          <option value="">Todos los profesionales</option>{profs.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </select>
        <div className="seg" role="group" aria-label="Vista">
          <button aria-pressed={vista === "dia"} onClick={() => setVista("dia")}>Día</button>
          <button aria-pressed={vista === "semana"} onClick={() => setVista("semana")}>Semana</button>
        </div>
      </div>
      <AvisoError error={a.error} />
      <div className="agenda-y-panel">
        <div className="grilla" style={{ ["--dias" as string]: dias.length }}>
          <div className="grilla-cab" />
          {dias.map((d, i) => (
            <button key={d} className={`grilla-cab dia${d === hoy() ? " hoy" : ""}${feriados[d] ? " feriado" : ""}`} title={feriados[d] ? `Feriado · ${feriados[d].nombre}${feriados[d].nota ? ` · ${feriados[d].nota}` : ""}` : undefined} onClick={() => { setDia(d); setVista("dia"); }}>
              {vista === "semana" ? DIAS_CORTOS[i] : fechaLarga(d).split(" ")[0]} <b>{Number(d.slice(8))}</b>
              {feriados[d] && <span className="feriado-tag">{vista === "dia" ? `Feriado · ${feriados[d].nombre}` : feriados[d].nombre}</span>}
            </button>
          ))}
          <div className="horas">{Array.from({ length: (FIN_GRILLA - INICIO_GRILLA) / 60 }, (_, i) => <span key={i} style={{ height: 60 * PX_MIN }}>{String(7 + i).padStart(2, "0")}:00</span>)}</div>
          {dias.map((d) => (
            <div key={d} className={`columna${feriados[d] ? " feriado" : ""}`} style={{ height: (FIN_GRILLA - INICIO_GRILLA) * PX_MIN }}
              onClick={(e) => {
                if (!escribe || e.target !== e.currentTarget) return;
                const y = e.nativeEvent.offsetY / PX_MIN + INICIO_GRILLA;
                const m = Math.floor(y / 15) * 15;
                nueva(d, `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
              }}>
              {d === ahora.fecha && minutos(ahora.hora) > INICIO_GRILLA && minutos(ahora.hora) < FIN_GRILLA && <div className="ahora" style={{ top: (minutos(ahora.hora) - INICIO_GRILLA) * PX_MIN }} />}
              {(() => {
                const delDia = citas.filter((c) => c.fecha === d);
                // si dos citas coinciden en la hora (por ejemplo con distintos profesionales) se ven una al lado de la otra
                const columnas = columnasDeCitas(delDia.map((c) => ({ id: c.id, inicio: minutos(c.hora), fin: minutos(c.hora) + Math.max(c.duracion, 15) })));
                return delDia.map((c) => {
                const top = Math.max(0, (minutos(c.hora) - INICIO_GRILLA) * PX_MIN), alto = Math.max(22, c.duracion * PX_MIN - 2);
                const { col, cols } = columnas.get(c.id) ?? { col: 0, cols: 1 };
                return (
                  <button key={c.id} className={`cita e-${c.estado}${sel?.id === c.id ? " sel" : ""}`} style={{ top, height: alto, left: `calc(${(col * 100) / cols}% + 3px)`, right: `calc(${((cols - col - 1) * 100) / cols}% + 3px)`, ["--c" as string]: colorDe[c.profesional_id] }} title={`${c.hora} · ${c.paciente} · ${c.profesional}`}
                    onClick={() => { setForm(null); setSel(c); }} aria-label={`${c.hora} ${c.paciente}, ${NOMBRE_ESTADO_CITA[c.estado]}${variasSedes && c.sede ? `, ${c.sede}` : ""}`}>
                    <b>{c.hora}</b>{" "}
                    <span className="cita-nombre" title="Abrir la ficha del paciente" onClick={(e) => { e.stopPropagation(); ir(`paciente/${c.paciente_id}`); }}>{c.paciente}</span>{c.paciente_nuevo && <span className="chip" title="Registro rápido: falta completar la ficha y la historia clínica">Nuevo</span>}{c.motivo && <span> · {c.motivo}</span>}{variasSedes && c.sede && <span className="sede-tag" title={c.sede}>{siglasSede(c.sede)}</span>}
                    {c.alerta_material && <span className="alerta-material" title={`Preparar para este control: ${c.alerta_material}`}> ⚠ material</span>}
                    {c.estado === "confirmada" && <span className="ok"> ✓</span>}
                  </button>
                );
              });
              })()}
            </div>
          ))}
        </div>
        {(sel || form) && (
          <aside className="card panel-cita">
            {sel && !form && <DetalleCita c={sel} conSedes={variasSedes} escribe={escribe} ocupado={a.ocupado} alEditar={() => editar(sel)} alEstado={(e, m) => cambiarEstado(sel, e, m)} alEliminar={() => eliminarCita(sel)} alCerrar={() => setSel(null)} ir={ir} />}
            {form && <FormCita f={form} profs={profs} sedes={sedes} alGuardar={(c) => { setForm(null); setSel(c); setDia(c.fecha); cargar(); }} alCerrar={() => setForm(null)} />}
          </aside>
        )}
      </div>
    </>
  );
}

/** Pedir confirmación (cita agendada) o avisar que quedó confirmada, por WhatsApp o correo, en este momento. */
function AvisoWhatsApp({ c }: { c: Cita }) {
  const [hecho, setHecho] = useState<string>(c.aviso_enviado ? `Recordatorio enviado el ${new Date(c.aviso_enviado).toLocaleString("es-EC", { dateStyle: "medium", timeStyle: "short" })}.` : "");
  const a = useAccion();
  const tipo = c.estado === "agendada" ? "recordatorio" : "confirmacion";
  const futura = new Date(c.inicio).getTime() > Date.now();
  if (!futura) return null;
  async function enviar() {
    const r = await a.correr(() => enviarMensajeAhora(`/api/citas/${c.id}/whatsapp`, { tipo }));
    if (r) setHecho(r.enviado
      ? `Enviado por ${r.canal === "whatsapp" ? "WhatsApp" : "correo"} a ${r.canal === "whatsapp" ? "+" : ""}${r.destino}.`
      : `${r.aviso ? r.aviso + " " : ""}Se abrió ${r.canal === "whatsapp" ? "WhatsApp" : "el correo"} con el mensaje listo: solo toca Enviar.${tipo === "recordatorio" ? " Cuando el paciente responda, marca la cita como confirmada." : ""}`);
  }
  return (
    <div className="aviso-wa">
      <button className="btn chico wa" disabled={a.ocupado} onClick={enviar}>
        {tipo === "recordatorio" ? "Pedir confirmación por WhatsApp" : "Avisar por WhatsApp que está confirmada"}
      </button>
      {hecho && <small className="muted" role="status">{hecho}</small>}
      <AvisoError error={a.error} />
    </div>
  );
}

function DetalleCita({ c, conSedes, escribe, ocupado, alEditar, alEstado, alEliminar, alCerrar, ir }: { c: Cita; conSedes: boolean; escribe: boolean; ocupado: boolean; alEditar: () => void; alEstado: (e: Cita["estado"], motivo?: string) => void; alEliminar: () => void; alCerrar: () => void; ir: (r: string) => void }) {
  const [motivo, setMotivo] = useState("");
  const [cancelando, setCancelando] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const pos = TRANSICIONES[c.estado];
  return (
    <>
      <div className="fila" style={{ justifyContent: "space-between" }}>
        <h2 style={{ fontSize: 18 }}><button className="enlace" style={{ font: "inherit", fontWeight: 700 }} title="Abrir la ficha del paciente" onClick={() => ir(`paciente/${c.paciente_id}`)}>{c.paciente}</button></h2>
        <button className="btn chico" onClick={alCerrar} aria-label="Cerrar">✕</button>
      </div>
      <p style={{ margin: 0 }}>{fechaLarga(c.fecha)} · <b>{horaLarga(c.hora)}</b> a {horaLarga(c.horaFin)} ({c.duracion} min)<br />{c.profesional}{c.motivo && <> · {c.motivo}</>}{conSedes && c.sede && <><br />Sede: <b>{c.sede}</b></>}</p>
      <span className={`chip e-${c.estado}`}>{NOMBRE_ESTADO_CITA[c.estado]}{c.estado === "confirmada" && c.confirmada_por === "paciente" ? " por el paciente" : ""}</span>
      {c.alerta_material && <div className="aviso error" role="alert"><b>Preparar material para este control:</b> {c.alerta_material}</div>}
      {c.notas && <p className="muted" style={{ margin: 0 }}>{c.notas}</p>}
      <div className="fila">
        <button className="btn chico" onClick={() => ir(`paciente/${c.paciente_id}/ficha`)}>Abrir ficha</button>
        {c.telefono && <a className="btn chico" href={`tel:${c.telefono}`}>Llamar</a>}
      </div>
      {escribe && ["agendada", "confirmada"].includes(c.estado) && <AvisoWhatsApp key={`${c.id}-${c.estado}`} c={c} />}
      {escribe && (
        <div className="fila">
          {pos.includes("confirmada") && <button className="btn chico pri" disabled={ocupado} onClick={() => alEstado("confirmada")}>Confirmar</button>}
          {pos.includes("atendida") && <button className="btn chico" disabled={ocupado} onClick={() => alEstado("atendida")}>Atendida</button>}
          {pos.includes("no_asistio") && <button className="btn chico" disabled={ocupado} onClick={() => alEstado("no_asistio")}>No asistió</button>}
          {pos.includes("agendada") && <button className="btn chico" disabled={ocupado} onClick={() => alEstado("agendada")}>Volver a agendada</button>}
          {["agendada", "confirmada"].includes(c.estado) && <button className="btn chico" onClick={alEditar}>Reprogramar</button>}
          {pos.includes("cancelada") && !cancelando && <button className="btn chico peligro" onClick={() => setCancelando(true)}>Cancelar cita</button>}
          {c.estado !== "atendida" && !eliminando && <button className="btn chico peligro" disabled={ocupado} onClick={() => { setCancelando(false); setEliminando(true); }}>Eliminar cita</button>}
        </div>
      )}
      {escribe && eliminando && (
        <div className="aviso error" role="alertdialog" aria-label="Confirmar eliminación">
          <b>¿Eliminar esta cita?</b> Desaparece de la agenda y no se envían recordatorios. Queda registrada en la auditoría. Para avisar al paciente de que no se atiende, usa mejor «Cancelar cita».
          <div className="fila" style={{ marginTop: 6 }}>
            <button className="btn chico peligro" disabled={ocupado} onClick={alEliminar}>Sí, eliminar</button>
            <button className="btn chico" disabled={ocupado} onClick={() => setEliminando(false)}>No, conservar</button>
          </div>
        </div>
      )}
      {cancelando && (
        <div className="fila">
          <input className="entrada" style={{ flex: 1 }} placeholder="Motivo (opcional)" value={motivo} onChange={(e) => setMotivo(e.target.value)} aria-label="Motivo de la cancelación" autoFocus />
          <button className="btn chico peligro" disabled={ocupado} onClick={() => alEstado("cancelada", motivo)}>Confirmar cancelación</button>
        </div>
      )}
    </>
  );
}

function FormCita({ f: inicial, profs, sedes, alGuardar, alCerrar }: { f: Formulario; profs: Profesional[]; sedes: Sede[]; alGuardar: (c: Cita) => void; alCerrar: () => void }) {
  const [f, setF] = useState(inicial);
  const [busqueda, setBusqueda] = useState("");
  const [opciones, setOpciones] = useState<Paciente[]>([]);
  const [huecos, setHuecos] = useState<string[]>([]);
  const [dictado, setDictado] = useState("");
  const [nuevo, setNuevo] = useState<{ nombres: string; apellidos: string; telefono: string } | null>(null);
  const [noEsDuplicado, setNoEsDuplicado] = useState(false);
  const [avisos, setAvisos] = useState<string[]>([]);
  const a = useAccion();
  useEffect(() => { setF(inicial); setAvisos([]); }, [inicial]);
  // si la fecha elegida es feriado, se avisa (no se bloquea: la clínica puede atender)
  const [feriadoDelDia, setFeriadoDelDia] = useState<FeriadoDia | null>(null);
  useEffect(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f.fecha)) { setFeriadoDelDia(null); return; }
    let vivo = true;
    api<FeriadoDia[]>("GET", `/api/agenda/feriados?desde=${f.fecha}&hasta=${f.fecha}`).then((l) => { if (vivo) setFeriadoDelDia(l[0] ?? null); }).catch(() => { if (vivo) setFeriadoDelDia(null); });
    return () => { vivo = false; };
  }, [f.fecha]);
  useEffect(() => {
    if (!busqueda.trim() || f.paciente) { setOpciones([]); return; }
    const t = setTimeout(() => api<Paciente[]>("GET", `/api/pacientes?q=${encodeURIComponent(busqueda)}`).then(setOpciones).catch(() => undefined), 200);
    return () => clearTimeout(t);
  }, [busqueda, f.paciente]);
  useEffect(() => {
    if (!f.profesionalId || !f.fecha) return;
    api<string[]>("GET", `/api/citas/huecos?fecha=${f.fecha}&profesional=${f.profesionalId}&duracion=${f.duracion}${f.sedeId ? `&sede=${f.sedeId}` : ""}`).then(setHuecos).catch(() => setHuecos([]));
  }, [f.profesionalId, f.fecha, f.duracion, f.sedeId]);

  async function aplicarDictado() {
    const d = interpretarCita(dictado, hoy());
    const nuevo = { ...f, ...(d.fecha ? { fecha: d.fecha } : {}), ...(d.hora ? { hora: d.hora } : {}), ...(d.duracion ? { duracion: d.duracion } : {}), ...(d.motivo ? { motivo: d.motivo } : {}) };
    if (d.nombre && !f.paciente) {
      const l = await api<Paciente[]>("GET", `/api/pacientes?q=${encodeURIComponent(d.nombre)}`).catch(() => []);
      const exacto = l.filter((p) => norm(`${p.nombres} ${p.apellidos}`).includes(norm(d.nombre)));
      if (exacto.length === 1) nuevo.paciente = { id: exacto[0].id, nombre: `${exacto[0].nombres} ${exacto[0].apellidos}` };
      else setBusqueda(d.nombre);
    }
    setF(nuevo); setDictado("");
  }
  async function crearNuevo() {
    if (!nuevo) return;
    const p = await a.correr(() => api<Paciente>("POST", "/api/pacientes", { rapido: true, nombres: nuevo.nombres, apellidos: nuevo.apellidos, telefono: nuevo.telefono, confirmarDuplicado: noEsDuplicado }));
    if (p) { set({ paciente: { id: p.id, nombre: `${p.nombres} ${p.apellidos}`.trim() } }); setNuevo(null); setBusqueda(""); setNoEsDuplicado(false); }
  }
  async function guardar(e: FormEvent, forzar = false) {
    e.preventDefault();
    if (!f.paciente) return a.setError(new ErrorApi(400, "Elige el paciente."));
    const cuerpo = { pacienteId: f.paciente.id, profesionalId: f.profesionalId, ...(f.sedeId ? { sedeId: f.sedeId } : {}), fecha: f.fecha, hora: f.hora, duracion: f.duracion, motivo: f.motivo, notas: f.notas, forzar };
    try {
      const c = await api<Cita>(f.id ? "PUT" : "POST", f.id ? `/api/citas/${f.id}` : "/api/citas", cuerpo);
      if (f.sedeId) escribirLocal("donna.agenda.ultima-sede", f.sedeId);
      alGuardar(c);
    } catch (err) {
      // fuera de horario o en el pasado: se puede agendar igual si el usuario lo confirma
      if (err instanceof ErrorApi && err.estado === 409 && err.detalles.length) { setAvisos([err.message, ...err.detalles]); a.setError(null); }
      else { setAvisos([]); a.setError(err); }
    }
  }
  const set = (x: Partial<Formulario>) => { setF({ ...f, ...x }); setAvisos([]); };

  return (
    <form onSubmit={guardar} style={{ display: "grid", gap: 10 }}>
      <div className="fila" style={{ justifyContent: "space-between" }}>
        <h2 style={{ fontSize: 18 }}>{f.id ? "Reprogramar cita" : "Nueva cita"}</h2>
        <button type="button" className="btn chico" onClick={alCerrar} aria-label="Cerrar">✕</button>
      </div>
      {!f.id && (
        <details>
          <summary style={{ cursor: "pointer" }}>Dictar la cita</summary>
          <Dictador texto={dictado} setTexto={setDictado} placeholder="Ej.: agenda a María Fernanda el jueves a las 10 para control" />
          <button type="button" className="btn chico" disabled={!dictado.trim()} onClick={aplicarDictado}>Completar con el dictado</button>
        </details>
      )}
      {f.paciente
        ? <div className="fila"><b style={{ flex: 1 }}>{f.paciente.nombre}</b>{!f.id && <button type="button" className="btn chico" onClick={() => set({ paciente: null })}>Cambiar</button>}</div>
        : (
          <div style={{ display: "grid", gap: 4 }}>
            <input className="entrada" placeholder="Buscar paciente…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} aria-label="Paciente" autoFocus />
            {opciones.length > 0 && <ul className="lista">{opciones.slice(0, 6).map((p) => <li key={p.id}><button type="button" onClick={() => { set({ paciente: { id: p.id, nombre: `${p.nombres} ${p.apellidos}` } }); setBusqueda(""); }}><span className="mono muted">HC {p.hc}</span> {p.nombres} {p.apellidos}</button></li>)}</ul>}
            {puede("pacientes:escribir") && !f.id && (nuevo
              ? (
                <div className="aviso info" style={{ display: "grid", gap: 8 }}>
                  <b>Paciente nuevo</b>
                  <span className="muted" style={{ fontSize: 13 }}>Solo nombres y celular. La cédula, la fecha de nacimiento y la historia clínica se llenan en la primera cita.</span>
                  <div className="fila">
                    <label className="campo" style={{ flex: "1 1 150px" }}>Nombres<input value={nuevo.nombres} maxLength={80} onChange={(e) => setNuevo({ ...nuevo, nombres: e.target.value })} autoFocus /></label>
                    <label className="campo" style={{ flex: "1 1 150px" }}>Apellidos (si los sabes)<input value={nuevo.apellidos} maxLength={80} onChange={(e) => setNuevo({ ...nuevo, apellidos: e.target.value })} /></label>
                    <label className="campo" style={{ flex: "1 1 130px" }}>Celular<input className="mono" inputMode="tel" placeholder="0991234567" value={nuevo.telefono} maxLength={20} onChange={(e) => setNuevo({ ...nuevo, telefono: e.target.value })} /></label>
                  </div>
                  <AvisoConDuplicado error={a.error} confirmado={noEsDuplicado} alCambiar={setNoEsDuplicado}
                    alUsar={(s) => { set({ paciente: { id: s.id, nombre: `${s.nombres} ${s.apellidos}`.trim() } }); setNuevo(null); setBusqueda(""); setNoEsDuplicado(false); a.setError(null); }} />
                  <div className="fila">
                    <button type="button" className="btn chico pri" disabled={a.ocupado || !nuevo.nombres.trim() || !nuevo.telefono.trim()} onClick={crearNuevo}>Crear y elegir</button>
                    <button type="button" className="btn chico" onClick={() => setNuevo(null)}>Cancelar</button>
                  </div>
                </div>
              )
              : <button type="button" className="btn chico" onClick={() => setNuevo({ nombres: busqueda.trim(), apellidos: "", telefono: "" })}>+ Paciente nuevo (solo nombres y celular)</button>)}
          </div>
        )}
      {sedes.length > 1 && <label className="campo">Sede<select value={f.sedeId} onChange={(e) => set({ sedeId: e.target.value })} required>{sedes.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}</select></label>}
      <label className="campo">Profesional<select value={f.profesionalId} onChange={(e) => set({ profesionalId: e.target.value })} required>{profs.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></label>
      <div className="fila">
        <label className="campo" style={{ flex: "1 1 140px" }}>Fecha<FechaInput value={f.fecha} onChange={(e) => set({ fecha: e.target.value })} required /></label>
        <label className="campo" style={{ flex: "1 1 100px" }}>Hora<input type="time" step={300} value={f.hora} onChange={(e) => set({ hora: e.target.value })} required /></label>
        <label className="campo" style={{ flex: "1 1 100px" }}>Duración<select value={f.duracion} onChange={(e) => set({ duracion: Number(e.target.value) })}>{DURACIONES.map((d) => <option key={d} value={d}>{d} min</option>)}</select></label>
      </div>
      {feriadoDelDia && <div className="aviso-feriado" role="status">Ese día es feriado: <b>{feriadoDelDia.nombre}</b>{feriadoDelDia.nota ? ` (${feriadoDelDia.nota.charAt(0).toLowerCase()}${feriadoDelDia.nota.slice(1)})` : ""}. Puedes agendar igual si la clínica atiende ese día.</div>}
      {huecos.length > 0 && <div className="fila" aria-label="Horas libres"><span className="muted" style={{ fontSize: 13 }}>Libres:</span>{huecos.slice(0, 8).map((h) => <button type="button" key={h} className={`btn chico${h === f.hora ? " pri" : ""}`} onClick={() => set({ hora: h })}>{h}</button>)}</div>}
      <label className="campo">Motivo<input value={f.motivo} maxLength={200} onChange={(e) => set({ motivo: e.target.value })} list="motivos-cita" /></label>
      <datalist id="motivos-cita">{["Control", "Valoración", "Limpieza", "Resina", "Endodoncia", "Exodoncia", "Control de ortodoncia", "Emergencia"].map((m) => <option key={m} value={m} />)}</datalist>
      <label className="campo">Notas<input value={f.notas} maxLength={1000} onChange={(e) => set({ notas: e.target.value })} /></label>
      {avisos.length > 0 && (
        <div className="aviso info">
          {avisos[0]}<ul>{avisos.slice(1).map((x) => <li key={x}>{x}</li>)}</ul>
          <button type="button" className="btn chico" onClick={(e) => guardar(e as unknown as FormEvent, true)}>Agendar de todas formas</button>
        </div>
      )}
      {!esErrorDuplicado(a.error) && <AvisoError error={a.error} />}
      <button className="btn pri" disabled={a.ocupado || !f.paciente}>{f.id ? "Guardar cambios" : "Agendar"}</button>
    </form>
  );
}

/** Citas de hoy para la pantalla de inicio. */
export function CitasDeHoy({ ir }: { ir: (r: string) => void }) {
  const [citas, setCitas] = useState<Cita[] | null>(() => leerCache<Cita[]>("inicio.citas") ?? null);
  const conSedes = (useClinica()?.sedes ?? []).filter((x) => x.activa).length > 1;
  useEffect(() => { if (puede("agenda:leer")) api<Cita[]>("GET", `/api/citas?desde=${hoy()}&hasta=${hoy()}`).then((c) => setCitas(guardarCache("inicio.citas", c))).catch(() => setCitas([])); }, []);
  if (!citas) return null;
  const vigentes = citas.filter((c) => c.estado !== "cancelada");
  return (
    <Seccion titulo={`Citas de hoy · ${vigentes.length}`} acciones={<button className="btn chico" onClick={() => ir("agenda")}>Ver agenda</button>}>
      {vigentes.length === 0 ? <p className="muted" style={{ margin: 0 }}>No hay citas para hoy.</p> : (
        <ul className="lista">{vigentes.map((c) => (
          <li key={c.id}><button onClick={() => ir(`paciente/${c.paciente_id}`)}>
            <span className="mono">{c.hora}</span><b>{c.paciente}</b>{c.paciente_nuevo && <span className="chip">Nuevo</span>}<span className="muted">{c.motivo} · {c.profesional}{conSedes && c.sede ? ` · ${c.sede}` : ""}</span>{c.alerta_material && <span className="chip rojo" title={c.alerta_material}>material</span>}
            <span className={`chip e-${c.estado}`} style={{ marginLeft: "auto" }}>{NOMBRE_ESTADO_CITA[c.estado]}</span>
          </button></li>
        ))}</ul>
      )}
    </Seccion>
  );
}
